/**
 * AdminReports — /admin/reports
 * Moderator queue for user-submitted content reports.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import ToolSelector from '../../components/ToolSelector';
import { Button } from '../../components/ui/button';
import { Flag, CheckCircle2, XCircle, Loader2, Filter } from 'lucide-react';

const STATUS_STYLE = {
  open:       'bg-red-100 text-red-800',
  reviewing:  'bg-amber-100 text-amber-800',
  resolved:   'bg-emerald-100 text-emerald-800',
  dismissed:  'bg-muted text-muted-foreground',
};

const AdminReports = () => {
  const { API } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('open');
  const [loading, setLoading] = useState(true);
  const [openCount, setOpenCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/moderation-reports?status=${status}&limit=200`);
      setRows(data.reports || []);
      setOpenCount(data.open_count || 0);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [API, status]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, newStatus, outcome) => {
    try {
      await axios.put(`${API}/admin/moderation-reports/${id}`, { status: newStatus, outcome });
      toast.success(`Report ${newStatus}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-reports-page">
      <ToolSelector />
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
        <div className="mb-6 flex items-start justify-between gap-3">
          <div>
            <div className="eyebrow text-primary inline-flex items-center gap-1.5">
              <Flag className="w-3 h-3" /> Admin · Reports
            </div>
            <h1 className="font-heading text-3xl font-extrabold mt-1">Community Reports</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              User-flagged posts, comments, DMs, and profiles for moderator review.
            </p>
          </div>
          {openCount > 0 && (
            <span className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-full bg-red-500 text-white flex-shrink-0" data-testid="admin-reports-open-count">
              {openCount} open
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-input bg-background px-2 py-1 text-xs" data-testid="admin-reports-filter">
            <option value="open">Open</option>
            <option value="reviewing">Reviewing</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="pro-card p-6 text-center text-sm text-muted-foreground italic">No reports in this state.</div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.id} className="pro-card p-4" data-testid={`admin-reports-row-${r.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status] || ''}`}>{r.status}</span>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{r.reason}</span>
                  <span className="text-[11px] text-muted-foreground ml-auto">{r.created_at?.slice(0, 16).replace('T', ' ')}</span>
                </div>
                <div className="font-heading font-extrabold text-sm">
                  {r.target_type} · {r.target_id?.slice(0, 12)}…
                </div>
                {r.notes && <div className="text-xs text-muted-foreground mt-1 italic">&ldquo;{r.notes}&rdquo;</div>}
                <div className="text-[11px] text-muted-foreground mt-2">Reported by {r.reporter_email}</div>
                {r.status === 'open' && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => update(r.id, 'reviewing')} className="rounded-full font-bold" data-testid={`admin-reports-review-${r.id}`}>
                      Start review
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => update(r.id, 'resolved', 'content_removed')} className="rounded-full" data-testid={`admin-reports-resolve-${r.id}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Resolve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => update(r.id, 'dismissed', 'no_violation')} className="rounded-full" data-testid={`admin-reports-dismiss-${r.id}`}>
                      <XCircle className="w-3 h-3 mr-1" /> Dismiss
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReports;
